import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  layout: {
    display: 'flex',
    gap: '32px',
    flexWrap: 'wrap',
  },
  chartContainer: {
    flexShrink: 0,
  },
  rightPanel: {
    flex: 1,
    minWidth: '200px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  legendList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  legendLabel: {
    color: '#374151',
    fontWeight: 500,
    minWidth: '50px',
  },
  legendCount: {
    color: '#6b7280',
    fontWeight: 600,
    minWidth: '24px',
  },
  legendBarOuter: {
    flex: 1,
    height: '6px',
    background: '#f3f4f6',
    borderRadius: '3px',
    overflow: 'hidden',
    minWidth: '60px',
  },
  legendAvg: {
    fontSize: '10px',
    color: '#9ca3af',
    whiteSpace: 'nowrap',
    minWidth: '55px',
    textAlign: 'right',
  },
  changeSectionTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginTop: '8px',
    marginBottom: '4px',
  },
  changeItems: {
    display: 'flex',
    gap: '16px',
    fontSize: '12px',
    flexWrap: 'wrap',
  },
  changeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  stackedBarOuter: {
    width: '100%',
    height: '8px',
    borderRadius: '4px',
    overflow: 'hidden',
    display: 'flex',
    marginTop: '6px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '220px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

const SVG_SIZE = 280;
const OUTER_RADIUS = 120;
const INNER_RADIUS = 70;
const HOVER_EXPAND = 8;

export default function PriceTierDonut({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    if (!data || !data.tiers || data.tiers.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg.attr('width', SVG_SIZE).attr('height', SVG_SIZE);

    const g = svg
      .append('g')
      .attr('transform', `translate(${SVG_SIZE / 2}, ${SVG_SIZE / 2})`);

    const pie = d3
      .pie()
      .value((d) => d.count)
      .sort(null)
      .padAngle(0.02);

    const arcDefault = d3.arc().innerRadius(INNER_RADIUS).outerRadius(OUTER_RADIUS);
    const arcHover = d3
      .arc()
      .innerRadius(INNER_RADIUS)
      .outerRadius(OUTER_RADIUS + HOVER_EXPAND);

    const arcs = pie(data.tiers);

    // Draw segments
    g.selectAll('.segment')
      .data(arcs)
      .enter()
      .append('path')
      .attr('d', arcDefault)
      .attr('fill', (d) => d.data.color)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('mouseenter', function (event, d) {
        d3.select(this).transition().duration(200).attr('d', arcHover);
        setHoveredIndex(d.index);
        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          tier: d.data,
        });
      })
      .on('mousemove', function (event, d) {
        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          tier: d.data,
        });
      })
      .on('mouseleave', function () {
        d3.select(this).transition().duration(200).attr('d', arcDefault);
        setHoveredIndex(null);
        setTooltip(null);
      });

    // Center text: total count
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '-0.1em')
      .attr('font-size', '24px')
      .attr('font-weight', '700')
      .attr('fill', '#1f2937')
      .text(data.totalAddresses);

    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '1.4em')
      .attr('font-size', '11px')
      .attr('fill', '#9ca3af')
      .text('addresses');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.tiers || data.tiers.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Distribution</div>
        <div style={styles.subtitle}>
          Address distribution across pest365 price tiers
        </div>
        <div style={styles.emptyState}>No tier distribution data available</div>
      </div>
    );
  }

  const { tiers, changeDistribution } = data;
  const changeTotal =
    changeDistribution
      ? changeDistribution.increased +
        changeDistribution.decreased +
        changeDistribution.unchanged
      : 0;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Distribution</div>
      <div style={styles.subtitle}>
        Address distribution across pest365 price tiers
      </div>
      <div style={styles.layout}>
        {/* Left: Donut chart */}
        <div style={styles.chartContainer}>
          <svg ref={svgRef} />
        </div>

        {/* Right: Legend + Stats */}
        <div style={styles.rightPanel}>
          <div style={styles.legendList}>
            {tiers.map((tier, i) => (
              <div
                key={tier.label}
                style={{
                  ...styles.legendItem,
                  opacity: hoveredIndex != null && hoveredIndex !== i ? 0.4 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                <span
                  style={{ ...styles.legendDot, background: tier.color }}
                />
                <span style={styles.legendLabel}>{tier.label}</span>
                <span style={styles.legendCount}>{tier.count}</span>
                <div style={styles.legendBarOuter}>
                  <div
                    style={{
                      width: `${tier.percent}%`,
                      height: '100%',
                      background: tier.color,
                      borderRadius: '3px',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
                <span style={styles.legendAvg}>
                  ${tier.avgPrice.toFixed(2)} avg
                </span>
              </div>
            ))}
          </div>

          {/* Price Change Distribution */}
          {changeDistribution && changeTotal > 0 && (
            <div>
              <div style={styles.changeSectionTitle}>
                Price Change Distribution
              </div>
              <div style={styles.changeItems}>
                <div style={styles.changeItem}>
                  <span style={{ color: '#16a34a', fontSize: '14px' }}>
                    &#9650;
                  </span>
                  <span style={{ color: '#374151' }}>
                    {changeDistribution.increased} increased
                  </span>
                </div>
                <div style={styles.changeItem}>
                  <span style={{ color: '#dc2626', fontSize: '14px' }}>
                    &#9660;
                  </span>
                  <span style={{ color: '#374151' }}>
                    {changeDistribution.decreased} decreased
                  </span>
                </div>
                <div style={styles.changeItem}>
                  <span style={{ color: '#9ca3af', fontSize: '14px' }}>
                    &#8212;
                  </span>
                  <span style={{ color: '#374151' }}>
                    {changeDistribution.unchanged} unchanged
                  </span>
                </div>
              </div>
              <div style={styles.stackedBarOuter}>
                {changeDistribution.increased > 0 && (
                  <div
                    style={{
                      width: `${(changeDistribution.increased / changeTotal) * 100}%`,
                      height: '100%',
                      background: '#16a34a',
                    }}
                  />
                )}
                {changeDistribution.decreased > 0 && (
                  <div
                    style={{
                      width: `${(changeDistribution.decreased / changeTotal) * 100}%`,
                      height: '100%',
                      background: '#dc2626',
                    }}
                  />
                )}
                {changeDistribution.unchanged > 0 && (
                  <div
                    style={{
                      width: `${(changeDistribution.unchanged / changeTotal) * 100}%`,
                      height: '100%',
                      background: '#d1d5db',
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: '#1f2937',
              marginBottom: '6px',
              fontSize: '13px',
            }}
          >
            {tooltip.tier.label}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Count</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.tier.count}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Percentage</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.tier.percent.toFixed(1)}%
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Avg Price</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              ${tooltip.tier.avgPrice.toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
